import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { useToast } from "../../contexts/ToastContext";
import { useListLeasesQuery } from "../../store/api/leasesApi";
import { useListPaymentsQuery } from "../../store/api/paymentsApi";
import {
	useDeleteUserMutation,
	useListUsersQuery,
} from "../../store/api/usersApi";
import CreateUserForm from "../user-management/components/CreateUserForm";
import QuickActions from "./components/QuickActions";
import RecentCommunications from "./components/RecentCommunications";
import TenantFilters from "./components/TenantFilters";
import TenantStatsCard from "./components/TenantStatsCard";
import TenantTable from "./components/TenantTable";

const TenantManagement = () => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [searchTerm, setSearchTerm] = useState("");
	const [propertyFilter, setPropertyFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [paymentFilter, setPaymentFilter] = useState("all");
	const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingTenant, setEditingTenant] = useState<any>(null);
	const [tenantToDelete, setTenantToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	// Fetch tenants from API
	const {
		data: usersData,
		isLoading: isLoadingTenants,
		refetch: refetchTenants,
	} = useListUsersQuery({
		role: "tenant",
		page_size: 1000,
	});

	// Fetch leases and payments for tenant data
	const { data: leasesData } = useListLeasesQuery({ page_size: 1000 });
	const { data: paymentsData } = useListPaymentsQuery({ page_size: 1000 });

	// Delete user mutation
	const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

	// Transform API data to tenant format
	const tenants = useMemo(() => {
		if (!usersData?.results) return [];

		return usersData.results.map((user) => {
			// Find active lease for this tenant
			const lease = leasesData?.results?.find(
				(l) => l.tenant_id === user.id && l.status === "active",
			);

			// Find recent payments for this tenant
			const tenantPayments =
				paymentsData?.results?.filter((p) => p.tenant_id === user.id) || [];
			const hasOverdue = tenantPayments.some((p) => p.status === "overdue");
			const paymentStatus = hasOverdue
				? "overdue"
				: tenantPayments.some((p) => p.status === "pending")
				? "pending"
				: "paid";

			return {
				id: user.id,
				name: `${user.first_name} ${user.last_name}`,
				email: user.email,
				phone: user.phone || "",
				property: lease?.property_name || user.property_address || "N/A",
				unit: user.unit_number || "N/A",
				status: user.is_active ? "active" : "inactive",
				paymentStatus,
				leaseStart: lease?.start_date || "",
				leaseEnd: lease?.end_date || "",
				monthlyRent: lease?.monthly_rent || 0,
				property_owner: user.property_owner || false,
			};
		});
	}, [usersData, leasesData, paymentsData]);

	// Filter tenants based on search and filters
	const filteredTenants = useMemo(
		() =>
			tenants.filter((tenant) => {
				const matchesSearch =
					tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					tenant.property?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					tenant.unit?.toLowerCase().includes(searchTerm.toLowerCase());

				const matchesProperty =
					propertyFilter === "all" || propertyFilter === tenant.property;
				const matchesStatus =
					statusFilter === "all" || tenant.status === statusFilter;
				const matchesPayment =
					paymentFilter === "all" || tenant.paymentStatus === paymentFilter;

				return (
					matchesSearch && matchesProperty && matchesStatus && matchesPayment
				);
			}),
		[tenants, searchTerm, propertyFilter, statusFilter, paymentFilter],
	);

	// Statistics with real data
	const stats = useMemo(
		() => ({
			totalTenants: tenants.length,
			activeTenants: tenants.filter((t) => t.status === "active").length,
			overduePayments: tenants.filter((t) => t.paymentStatus === "overdue")
				.length,
			leasesExpiring: tenants.filter((t) => {
				if (!t.leaseEnd) return false;
				const leaseEnd = new Date(t.leaseEnd);
				const thirtyDaysFromNow = new Date();
				thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
				return leaseEnd <= thirtyDaysFromNow && leaseEnd >= new Date();
			}).length,
		}),
		[tenants],
	);

	const handleSelectTenant = (tenantId: string) => {
		setSelectedTenants((prev) =>
			prev.includes(tenantId)
				? prev.filter((id) => id !== tenantId)
				: [...prev, tenantId],
		);
	};

	const handleSelectAll = () => {
		if (selectedTenants.length === filteredTenants.length) {
			setSelectedTenants([]);
		} else {
			setSelectedTenants(filteredTenants.map((t) => t.id));
		}
	};

	const handleClearFilters = () => {
		setSearchTerm("");
		setPropertyFilter("all");
		setStatusFilter("all");
		setPaymentFilter("all");
	};

	const handleAddTenant = () => {
		setEditingTenant(null);
		setShowCreateForm(true);
	};

	const handleEditTenant = (tenant: any) => {
		// Find the original user object from usersData
		const originalUser = usersData?.results?.find((u) => u.id === tenant.id);
		if (originalUser) {
			setEditingTenant(originalUser);
			setShowCreateForm(true);
		} else {
			showError("Unable to load tenant data. Please try again.");
		}
	};

	const handleDeleteTenant = (tenantId: string, tenantName: string) => {
		setTenantToDelete({ id: tenantId, name: tenantName });
		setIsDeleteModalOpen(true);
	};

	const confirmDeleteTenant = async () => {
		if (!tenantToDelete) return;

		try {
			await deleteUser(tenantToDelete.id).unwrap();
			refetchTenants();
			setSelectedTenants((prev) =>
				prev.filter((id) => id !== tenantToDelete.id),
			);
			success(`Tenant "${tenantToDelete.name}" deleted successfully`);
			setIsDeleteModalOpen(false);
			setTenantToDelete(null);
		} catch (error: any) {
			console.error("Failed to delete tenant:", error);
			showError(
				error?.data?.detail || "Failed to delete tenant. Please try again.",
			);
			setIsDeleteModalOpen(false);
			setTenantToDelete(null);
		}
	};

	const handleFormSuccess = () => {
		setShowCreateForm(false);
		setEditingTenant(null);
		refetchTenants();
		success(
			editingTenant
				? "Tenant updated successfully"
				: "Tenant created successfully",
		);
	};

	const handleFormCancel = () => {
		setShowCreateForm(false);
		setEditingTenant(null);
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
							Tenant Management
						</h1>
						<p className="text-muted-foreground">
							Manage tenant profiles, leases, and communications
						</p>
					</div>
					<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
						<Button
							variant="outline"
							iconName="Download"
							iconPosition="left"
							className="w-full sm:w-auto"
							onClick={() => {
								const token = localStorage.getItem('access_token');
								if (token) {
									window.open(`${import.meta.env.VITE_API_BASE_URL}/export/users?role=tenant`, '_blank');
								} else {
									showError('Please log in to export data');
								}
							}}
						>
							Export Data
						</Button>
						<Button
							variant="default"
							iconName="UserPlus"
							iconPosition="left"
							onClick={handleAddTenant}
							className="w-full sm:w-auto"
						>
							Add Tenant
						</Button>
					</div>
				</div>

				{/* Create Tenant Form */}
				{showCreateForm && (
					<div className="bg-card rounded-lg border border-border p-6 mb-6">
						<div className="mb-4">
							<h2 className="text-xl font-semibold text-foreground">
								{editingTenant ? "Edit Tenant" : "Add New Tenant"}
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								{editingTenant
									? "Update tenant information below"
									: "Fill in the details to create a new tenant account"}
							</p>
						</div>
						<CreateUserForm
							onSuccess={handleFormSuccess}
							onCancel={handleFormCancel}
							user={editingTenant}
						/>
					</div>
				)}

				<div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6">
					{/* Main Content */}
					<div className="xl:col-span-9 space-y-4 sm:space-y-6">
						{/* Statistics Cards */}
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							<TenantStatsCard
								title="Total Tenants"
								value={stats.totalTenants}
								icon="Users"
								color="primary"
								change="+2"
								changeType="increase"
							/>
							<TenantStatsCard
								title="Active Tenants"
								value={stats.activeTenants}
								icon="UserCheck"
								color="success"
								change="+1"
								changeType="increase"
							/>
							<TenantStatsCard
								title="Overdue Payments"
								value={stats.overduePayments}
								icon="AlertTriangle"
								color="error"
								change="-1"
								changeType="decrease"
							/>
							<TenantStatsCard
								title="Expiring Leases"
								value={stats.leasesExpiring}
								icon="Calendar"
								color="warning"
								change="0"
								changeType="increase"
							/>
						</div>

						{/* Filters */}
						<TenantFilters
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							propertyFilter={propertyFilter}
							setPropertyFilter={setPropertyFilter}
							statusFilter={statusFilter}
							setStatusFilter={setStatusFilter}
							paymentFilter={paymentFilter}
							setPaymentFilter={setPaymentFilter}
							onClearFilters={handleClearFilters}
						/>

						{/* Bulk Actions */}
						{selectedTenants.length > 0 && (
							<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
								<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
									<p className="text-sm text-primary">
										{selectedTenants.length} tenant
										{selectedTenants.length > 1 ? "s" : ""} selected
									</p>
									<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											iconName="Mail"
											iconPosition="left"
											className="w-full sm:w-auto"
										>
											Send Email
										</Button>
										<Button
											variant="outline"
											size="sm"
											iconName="MessageSquare"
											iconPosition="left"
											className="w-full sm:w-auto"
										>
											Send SMS
										</Button>
										<Button
											variant="outline"
											size="sm"
											iconName="FileText"
											iconPosition="left"
											className="w-full sm:w-auto"
										>
											Generate Report
										</Button>
									</div>
								</div>
							</div>
						)}

						{/* Tenant Table */}
						<TenantTable
							tenants={filteredTenants}
							selectedTenants={selectedTenants}
							onSelectTenant={handleSelectTenant}
							onSelectAll={handleSelectAll}
							onEdit={handleEditTenant}
							onDelete={handleDeleteTenant}
						/>

						{/* Results Summary */}
						<div className="flex items-center justify-between text-sm text-muted-foreground">
							<p>
								Showing {filteredTenants.length} of {tenants.length} tenants
							</p>
							<p>Last updated: {new Date().toLocaleString()}</p>
						</div>
					</div>

					{/* Right Sidebar */}
					<div className="xl:col-span-3 space-y-4 sm:space-y-6">
						<RecentCommunications />
						<QuickActions onAddTenant={handleAddTenant} tenants={tenants} />
					</div>
				</div>

				{/* Delete Confirmation Modal */}
				<ConfirmationModal
					isOpen={isDeleteModalOpen}
					onClose={() => {
						setIsDeleteModalOpen(false);
						setTenantToDelete(null);
					}}
					onConfirm={confirmDeleteTenant}
					title="Delete Tenant"
					message={
						tenantToDelete
							? `Are you sure you want to delete tenant "${tenantToDelete.name}"? This action cannot be undone.`
							: ""
					}
					confirmButtonText="Delete"
					isDestructive={true}
				/>
			</div>
		</div>
	);
};

export default TenantManagement;
